import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, alpha, darken, lighten, useTheme } from "@material-ui/core/styles";
import { Button, Typography, Chip } from "@material-ui/core";
import { Card, CardActions, CardContent, CardMedia } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

// useStyles is the hook to Material-UI's style.
const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(1),
    maxWidth: "400px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  cardDisabled: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  image: {
    height: 200,
    backgroundPosition: "center",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 700,
  },
  betaChip: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 600,
  },
  iconBadge: {
    flexShrink: 0,
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
  content: {
    flexGrow: 1,
  },
  description: {
    paddingTop: theme.spacing(1),
  },
  cardDescription: {
    color: theme.palette.text.secondary,
  },
  actions: {
    padding: theme.spacing(1, 2, 2),
  },
  chooseIcon: {
    marginLeft: theme.spacing(0.5),
    fontSize: "1.1rem",
  },
}));

// this is a custom component for the acquisition form
// it is a card with the module's color, icon and a button to choose it
const AlgorithmCard = ({ name, Icon, color = "#26A69A", image, description, onChoose, enabled, beta }) => {
  // use the translation function from react-i18next
  const [t] = useTranslation();
  // use the styles from the useStyles hook
  const classes = useStyles();
  const theme = useTheme();
  const ink = (amount) => (theme.palette.type === "dark" ? lighten(color, amount) : darken(color, amount));

  return (
    <Card
      className={`${classes.card} ${enabled ? "" : classes.cardDisabled}`}
      style={{ minHeight: 500, cursor: enabled ? "pointer" : "default" }}
      onClick={enabled ? onChoose : undefined}
    >
      {image && (
        <CardMedia
          className={classes.image}
          image={image}
          title={name}
          style={{ borderBottom: `5px solid ${color}` }}
        />
      )}
      <div className={classes.header} style={{ borderBottom: `1px solid ${alpha(color, 0.35)}` }}>
        <div className={classes.titleRow}>
          <Typography variant="h5" component="h2" className={classes.title}>
            {name}
          </Typography>
          {beta && <Chip label={t("forms.algorithm.beta")} size="small" className={classes.betaChip} />}
        </div>
        {Icon && (
          <div className={classes.iconBadge} style={{ backgroundColor: alpha(color, 0.14) }}>
            <Icon color={ink(0.3)} />
          </div>
        )}
      </div>
      <CardContent className={classes.content}>
        <Typography component="p" className={classes.cardDescription}>
          {description}
        </Typography>
      </CardContent>
      <CardActions className={classes.actions}>
        <Button
          style={enabled ? { color: ink(0.35) } : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onChoose();
          }}
          disabled={!enabled}
        >
          {t("forms.acquisition.1.card.choose")}
          <ArrowForwardIcon className={classes.chooseIcon} />
        </Button>
      </CardActions>
    </Card>
  );
};

export default AlgorithmCard;
